import React, { useState } from "react";
import { Card, Input, Button, Table, message, Spin, Row, Col, Typography, Tag } from "antd";
import { SearchOutlined, ShopOutlined, ShoppingOutlined, DollarCircleOutlined } from "@ant-design/icons";
import API from "../../api";

const { Title } = Typography;

const Tracking = () => {
    const [trackingNumber, setTrackingNumber] = useState("");
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState(null);

    const handleSearch = async () => {
        if (!trackingNumber.trim()) {
            message.warning("Please enter a tracking number");
            return;
        }

        try {
            setLoading(true);
            const response = await API.get(`/tracking/search?trackingNumber=${trackingNumber.trim()}`);
            
            if (response.data.success) {
                setSearchResults(response.data);
                message.success(response.data.message);
            } else {
                message.error(response.data.message || "Search failed");
                setSearchResults(null);
            }
        } catch (error) {
            console.error("Search error:", error);
            message.error("Failed to search tracking number");
            setSearchResults(null);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const stockColumns = [
        {
            title: "Store Name",
            dataIndex: "storeName",
            key: "storeName",
        },
        {
            title: "Product Name",
            dataIndex: "productName",
            key: "productName",
        },
        {
            title: "Batch Number",
            dataIndex: "batchNumber",
            key: "batchNumber",
        },
        {
            title: "Tracking Number",
            dataIndex: "trackingNumber",
            key: "trackingNumber",
        },
        {
            title: "Quantity",
            dataIndex: "quantity",
            key: "quantity",
            render: (quantity) => quantity || 0,
        },
        {
            title: "Purchase Price",
            dataIndex: "purchasePrice",
            key: "purchasePrice",
            render: (price) => `₹ ${price || 0}`,
        },
        {
            title: "Selling Price",
            dataIndex: "sellingPrice",
            key: "sellingPrice",
            render: (price) => `₹ ${price || 0}`,
        },
        {
            title: "MRP",
            dataIndex: "mrp",
            key: "mrp",
            render: (mrp) => `₹ ${mrp || 0}`,
        },
    ];

    const purchaseColumns = [
        {
            title: "Purchase Invoice ID",
            dataIndex: "purchaseInvoiceId",
            key: "purchaseInvoiceId",
        },
        {
            title: "Purchase Date",
            dataIndex: "purchaseDate",
            key: "purchaseDate",
            render: (date) => date ? new Date(date).toLocaleDateString() : "N/A",
        },
        {
            title: "Store Name",
            dataIndex: "storeName",
            key: "storeName",
        },
        {
            title: "Product Name",
            dataIndex: "productName",
            key: "productName",
        },
        {
            title: "Batch Number",
            dataIndex: "batchNumber",
            key: "batchNumber",
        },
        {
            title: "Tracking Number",
            dataIndex: "trackingNumber",
            key: "trackingNumber",
        },
        {
            title: "Quantity",
            dataIndex: "quantity",
            key: "quantity",
            render: (quantity) => quantity && quantity > 0 ? quantity : "N/A",
        },
        {
            title: "Purchase Price",
            dataIndex: "purchasePrice",
            key: "purchasePrice",
            render: (price) => price && price > 0 ? `₹ ${price}` : "N/A",
        },
    ];

    const salesColumns = [
        {
            title: "Sales Invoice ID",
            dataIndex: "salesInvoiceId",
            key: "salesInvoiceId",
        },
        {
            title: "Order Date",
            dataIndex: "orderDate",
            key: "orderDate",
            render: (date) => date ? new Date(date).toLocaleDateString() : "N/A",
        },
        {
            title: "Store Name",
            dataIndex: "storeName",
            key: "storeName",
        },
        {
            title: "Product Name",
            dataIndex: "productName",
            key: "productName",
        },
        {
            title: "Batch Number",
            dataIndex: "batchNumber",
            key: "batchNumber",
        },
        {
            title: "Tracking Number",
            dataIndex: "trackingNumber",
            key: "trackingNumber",
        },
        {
            title: "Quantity",
            dataIndex: "quantity",
            key: "quantity",
            render: (quantity) => quantity && quantity > 0 ? quantity : "N/A",
        },
        {
            title: "Sale Price",
            dataIndex: "salePrice",
            key: "salePrice",
            render: (price) => price && price > 0 ? `₹ ${price}` : "N/A",
        },
    ];

    return (
        <div style={{ padding: "20px" }}>
            <Card
                title="Tracking"
                style={{
                    borderRadius: "10px",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                }}
            >
                <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
                    <Col span={16}>
                        <Input
                            placeholder="Enter Tracking Number"
                            value={trackingNumber}
                            onChange={(e) => setTrackingNumber(e.target.value)}
                            onKeyPress={handleKeyPress}
                            size="large"
                        />
                    </Col>
                    <Col span={8}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            loading={loading}
                            size="large"
                            style={{ width: "100%" }}
                        >
                            Search
                        </Button>
                    </Col>
                </Row>

                {loading && (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: "16px" }}>Searching...</p>
                    </div>
                )}

                {searchResults && !loading && (
                    <div>
                        <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
                            <Col span={6}>
                                <Card
                                    title={<><ShopOutlined style={{ marginRight: 8, color: "green" }} />In Stock</>}
                                    bordered
                                    style={{ background: "#f6ffed" }}
                                >
                                    <div style={{ textAlign: "center" }}>
                                        <Title level={2} style={{ color: "green", margin: 0 }}>
                                            {searchResults.summary?.stockCount || 0}
                                        </Title>
                                        <p style={{ margin: 0 }}>Items</p>
                                    </div>
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card
                                    title={<><ShoppingOutlined style={{ marginRight: 8, color: "blue" }} />In Purchase</>}
                                    bordered
                                    style={{ background: "#e6f7ff" }}
                                >
                                    <div style={{ textAlign: "center" }}>
                                        <Title level={2} style={{ color: "blue", margin: 0 }}>
                                            {searchResults.summary?.purchaseCount || 0}
                                        </Title>
                                        <p style={{ margin: 0 }}>Records</p>
                                    </div>
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card
                                    title={<><DollarCircleOutlined style={{ marginRight: 8, color: "orange" }} />In Sales</>}
                                    bordered
                                    style={{ background: "#fff7e6" }}
                                >
                                    <div style={{ textAlign: "center" }}>
                                        <Title level={2} style={{ color: "orange", margin: 0 }}>
                                            {searchResults.summary?.salesCount || 0}
                                        </Title>
                                        <p style={{ margin: 0 }}>Records</p>
                                    </div>
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card
                                    title="Total Found"
                                    bordered
                                    style={{ background: "#f9f0ff" }}
                                >
                                    <div style={{ textAlign: "center" }}>
                                        <Title level={2} style={{ color: "purple", margin: 0 }}>
                                            {searchResults.summary?.totalCount || 0}
                                        </Title>
                                        <p style={{ margin: 0 }}>
                                            <Tag color="purple">All Records</Tag>
                                        </p>
                                    </div>
                                </Card>
                            </Col>
                        </Row>

                        {/* In Stock Section - Always Visible */}
                        <Card
                            title={<><ShopOutlined style={{ marginRight: 8, color: "green" }} />In Stock ({searchResults.inStock?.length || 0} item{(searchResults.inStock?.length || 0) !== 1 ? 's' : ''})</>}
                            style={{ marginBottom: "20px" }}
                        >
                            {searchResults.inStock && searchResults.inStock.length > 0 ? (
                                <Table
                                    dataSource={searchResults.inStock}
                                    columns={stockColumns}
                                    rowKey="stockId"
                                    pagination={false}
                                    bordered
                                    scroll={{ x: 800 }}
                                    size="small"
                                />
                            ) : (
                                <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                                    No records found in stock for tracking number: <strong>{searchResults.trackingNumber}</strong>
                                </div>
                            )}
                        </Card>

                        {/* In Purchase Section - Always Visible */}
                        <Card
                            title={<><ShoppingOutlined style={{ marginRight: 8, color: "blue" }} />In Purchase ({searchResults.inPurchase?.length || 0} record{(searchResults.inPurchase?.length || 0) !== 1 ? 's' : ''})</>}
                            style={{ marginBottom: "20px" }}
                        >
                            {searchResults.inPurchase && searchResults.inPurchase.length > 0 ? (
                                <Table
                                    dataSource={searchResults.inPurchase}
                                    columns={purchaseColumns}
                                    rowKey="purchaseId"
                                    pagination={false}
                                    bordered
                                    scroll={{ x: 800 }}
                                    size="small"
                                />
                            ) : (
                                <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                                    No records found in purchase invoices for tracking number: <strong>{searchResults.trackingNumber}</strong>
                                </div>
                            )}
                        </Card>

                        {/* In Sales Section - Always Visible */}
                        <Card
                            title={<><DollarCircleOutlined style={{ marginRight: 8, color: "orange" }} />In Sales ({searchResults.inSales?.length || 0} record{(searchResults.inSales?.length || 0) !== 1 ? 's' : ''})</>}
                            style={{ marginBottom: "20px" }}
                        >
                            {searchResults.inSales && searchResults.inSales.length > 0 ? (
                                <Table
                                    dataSource={searchResults.inSales}
                                    columns={salesColumns}
                                    rowKey="salesId"
                                    pagination={false}
                                    bordered
                                    scroll={{ x: 800 }}
                                    size="small"
                                />
                            ) : (
                                <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                                    No records found in sales invoices for tracking number: <strong>{searchResults.trackingNumber}</strong>
                                </div>
                            )}
                        </Card>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default Tracking;